import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Plus, Upload, Edit, Copy, Trash2, 
  ChefHat, Users, Flame, Beef, CheckSquare, Square,
  FolderPlus, Folder, X, ExternalLink, Clock, Wheat, Droplet, AlertTriangle, RefreshCcw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import AdminNav from '../../components/admin/AdminNav';
import { cap } from './mealLabel';

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, count }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border rounded-xl p-6 max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/10 rounded-full">
            <AlertTriangle className="text-red-500" size={24} />
          </div>
          <h3 className="font-bold text-lg">Delete {count} Meal{count > 1 ? 's' : ''}?</h3>
        </div>
        <p className="text-muted-foreground mb-6">
          Are you sure you want to delete {count} selected meal{count > 1 ? 's' : ''}? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="confirm-bulk-delete-btn">
            <Trash2 size={16} className="mr-1" />Delete
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

const AdminMealList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meals, setMeals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  
  // Bulk selection state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState(new Set());
  
  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mealsToDelete, setMealsToDelete] = useState([]);
  
  // Meal groups state
  const [groups, setGroups] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#6366f1');
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  
  // Uniqueness groups state
  const [uniquenessGroupFilter, setUniquenessGroupFilter] = useState('');
  const [uniquenessGroups, setUniquenessGroups] = useState([]);
  const [showUniquenessOverview, setShowUniquenessOverview] = useState(false);
  const [uniquenessOverview, setUniquenessOverview] = useState(null);
  const [newUniquenessGroupName, setNewUniquenessGroupName] = useState('');
  const [addingNewGroup, setAddingNewGroup] = useState(false);
  
  // Add to repeat rules modal state
  const [showAddToRepeatRulesModal, setShowAddToRepeatRulesModal] = useState(false);
  const [selectedRepeatGroup, setSelectedRepeatGroup] = useState('');
  const [repeatNoSameDay, setRepeatNoSameDay] = useState(true);
  const [repeatCooldownDays, setRepeatCooldownDays] = useState(3);

  // Check admin access on mount
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await adminAPI.checkAdmin();
        if (!res.data.is_admin) {
          toast.error('Not authorized');
          navigate('/home');
          return;
        }
        setIsAdmin(true);
      } catch (err) {
        console.error('Admin check failed:', err);
        toast.error('Not authorized');
        navigate('/home');
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAccess();
  }, [navigate]);

  // Fetch meal groups
  const fetchGroups = useCallback(async () => {
    try {
      const res = await adminAPI.listMealGroups();
      setGroups(res.data || []);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchGroups();
  }, [isAdmin, fetchGroups]);

  // Fetch uniqueness groups
  const fetchUniquenessGroups = useCallback(async () => {
    try {
      const res = await adminAPI.listUniquenessGroups();
      setUniquenessGroups(res.data || []);
    } catch (err) {
      console.error('Failed to fetch uniqueness groups:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchUniquenessGroups();
  }, [isAdmin, fetchUniquenessGroups]);

  // Fetch uniqueness groups overview when modal is opened
  useEffect(() => {
    if (!showUniquenessOverview || !isAdmin) return;
    const fetchOverview = async () => {
      try {
        const res = await adminAPI.getUniquenessGroupsOverview();
        setUniquenessOverview(res.data);
      } catch (err) {
        console.error('Failed to fetch uniqueness overview:', err);
        toast.error('Failed to load overview');
      }
    };
    fetchOverview();
  }, [showUniquenessOverview, isAdmin]);

  // Fetch meals when filters change
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchMeals = async () => {
      setLoading(true);
      try {
        const res = await adminAPI.listMeals(
          searchQuery || undefined, 
          statusFilter || undefined, 
          sourceFilter || undefined,
          groupFilter || undefined,
          uniquenessGroupFilter || undefined
        );
        setMeals(res.data || []);
      } catch (err) {
        console.error('Failed to fetch meals:', err);
        toast.error('Failed to load meals');
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchMeals, 300);
    return () => clearTimeout(debounce);
  }, [isAdmin, searchQuery, statusFilter, sourceFilter, groupFilter, uniquenessGroupFilter]);

  const handleDuplicate = async (mealId, e) => {
    e.stopPropagation();
    try {
      const res = await adminAPI.duplicateMeal(mealId);
      toast.success('Meal duplicated');
      const refreshRes = await adminAPI.listMeals(searchQuery || undefined, statusFilter || undefined, sourceFilter || undefined, groupFilter || undefined, uniquenessGroupFilter || undefined);
      setMeals(refreshRes.data || []);
      navigate(`/admin/meals/${res.data.meal_id}/edit`);
    } catch (err) {
      console.error('Failed to duplicate:', err);
      toast.error('Failed to duplicate meal');
    }
  };

  const handleDelete = async (mealId, title, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteMeal(mealId);
      toast.success('Meal deleted');
      setMeals(meals.filter(m => m.meal_id !== mealId));
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete meal');
    }
  };

  // Bulk selection handlers
  const toggleSelectMode = () => {
    if (selectMode) {
      // Exiting select mode - clear selections
      setSelectedMeals(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleMealSelection = (mealId) => {
    const newSelected = new Set(selectedMeals);
    if (newSelected.has(mealId)) {
      newSelected.delete(mealId);
    } else {
      newSelected.add(mealId);
    }
    setSelectedMeals(newSelected);
  };

  const selectAll = () => {
    if (!selectMode) return;
    const allSelected = meals.every(m => selectedMeals.has(m.meal_id));
    
    if (allSelected) {
      // Deselect all
      setSelectedMeals(new Set());
      toast.info('Deselected all meals');
    } else {
      // Select all currently displayed meals
      const newSelected = new Set(meals.map(m => m.meal_id));
      setSelectedMeals(newSelected);
      toast.success(`Selected all ${meals.length} meals`);
    }
  };

  const selectAllInSource = (source) => {
    if (!selectMode) return;
    const mealsInSource = meals.filter(m => m.source === source);
    const allSelected = mealsInSource.every(m => selectedMeals.has(m.meal_id));
    
    const newSelected = new Set(selectedMeals);
    if (allSelected) {
      // Deselect all in this source
      mealsInSource.forEach(m => newSelected.delete(m.meal_id));
    } else {
      // Select all in this source
      mealsInSource.forEach(m => newSelected.add(m.meal_id));
    }
    setSelectedMeals(newSelected);
  };

  const selectAllInGroup = (groupId) => {
    if (!selectMode) return;
    
    // Filter meals that belong to this specific group
    const mealsInGroup = meals.filter(m => m.group_id === groupId);
    
    if (mealsInGroup.length === 0) {
      toast.info('No meals in this group');
      return;
    }
    
    const allSelected = mealsInGroup.every(m => selectedMeals.has(m.meal_id));
    
    const newSelected = new Set(selectedMeals);
    if (allSelected) {
      mealsInGroup.forEach(m => newSelected.delete(m.meal_id));
      toast.info(`Deselected ${mealsInGroup.length} meals from ${groups.find(g => g.group_id === groupId)?.name || 'group'}`);
    } else {
      mealsInGroup.forEach(m => newSelected.add(m.meal_id));
      toast.success(`Selected ${mealsInGroup.length} meals from ${groups.find(g => g.group_id === groupId)?.name || 'group'}`);
    }
    setSelectedMeals(newSelected);
  };

  const handleBulkDelete = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (selectedMeals.size === 0) {
      toast.error('No meals selected');
      return;
    }
    
    // Show the delete confirmation modal for all selected meals
    const selectedArray = [...selectedMeals];
    setMealsToDelete(selectedArray);
    setShowDeleteConfirm(true);
  };
  
  const confirmBulkDelete = async () => {
    setShowDeleteConfirm(false);
    
    try {
      const res = await adminAPI.bulkDeleteMeals(mealsToDelete);
      toast.success(`Deleted ${res.data.deleted_count} meals`);
      setMeals(meals.filter(m => !mealsToDelete.includes(m.meal_id)));
      setSelectedMeals(new Set());
      setSelectMode(false);
      setMealsToDelete([]);
      // Refresh groups to update counts
      fetchGroups();
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      toast.error('Failed to delete meals');
    }
  };

  // Meal group handlers
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    try {
      await adminAPI.createMealGroup({ name: newGroupName, color: newGroupColor });
      toast.success('Group created');
      setShowGroupModal(false);
      setNewGroupName('');
      fetchGroups();
    } catch (err) {
      console.error('Failed to create group:', err);
      toast.error('Failed to create group');
    }
  };

  const handleAddToGroup = async (groupId) => {
    const v2Meals = [...selectedMeals].filter(id => id.startsWith('meal_v2_'));
    if (v2Meals.length === 0) {
      toast.error('Only admin-created meals can be added to groups');
      return;
    }
    
    try {
      await adminAPI.addMealsToGroup(v2Meals, groupId);
      toast.success(`Added ${v2Meals.length} meals to group`);
      setShowAddToGroupModal(false);
      setSelectedMeals(new Set());
      setSelectMode(false);
      // Refresh both meals and groups
      const [mealsRes] = await Promise.all([
        adminAPI.listMeals(searchQuery || undefined, statusFilter || undefined, sourceFilter || undefined, groupFilter || undefined),
        fetchGroups()
      ]);
      setMeals(mealsRes.data || []);
    } catch (err) {
      console.error('Failed to add to group:', err);
      toast.error('Failed to add meals to group');
    }
  };

  const handleAddToRepeatRules = async () => {
    const v2Meals = [...selectedMeals].filter(id => id.startsWith('meal_v2_'));
    if (v2Meals.length === 0) {
      toast.error('Only admin-created meals can be added to repeat rules');
      return;
    }
    
    // Determine the group name
    let groupName = selectedRepeatGroup;
    if (selectedRepeatGroup === '__new__') {
      if (!newUniquenessGroupName.trim()) {
        toast.error('Please enter a name for the new group');
        return;
      }
      groupName = newUniquenessGroupName.trim();
    }
    
    if (!groupName) {
      toast.error('Please select a repeat rules group');
      return;
    }
    
    try {
      await adminAPI.bulkAssignUniquenessGroup(
        v2Meals, 
        groupName, 
        repeatNoSameDay, 
        repeatCooldownDays
      );
      toast.success(`Added ${v2Meals.length} meals to "${groupName}" repeat rules`);
      setShowAddToRepeatRulesModal(false);
      setSelectedMeals(new Set());
      setSelectMode(false);
      setSelectedRepeatGroup('');
      setNewUniquenessGroupName('');
      // Refresh meals and uniqueness groups
      const [mealsRes, groupsRes] = await Promise.all([
        adminAPI.listMeals(searchQuery || undefined, statusFilter || undefined, sourceFilter || undefined, groupFilter || undefined),
        adminAPI.listUniquenessGroups()
      ]);
      setMeals(mealsRes.data || []);
      setUniquenessGroups(groupsRes.data || []);
    } catch (err) {
      console.error('Failed to add to repeat rules:', err);
      toast.error('Failed to add meals to repeat rules');
    }
  };

  const handleDeleteGroup = async (groupId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this group? Meals will not be deleted.')) return;
    
    try {
      await adminAPI.deleteMealGroup(groupId);
      toast.success('Group deleted');
      setGroups(groups.filter(g => g.group_id !== groupId));
      if (groupFilter === groupId) setGroupFilter('');
    } catch (err) {
      console.error('Failed to delete group:', err);
      toast.error('Failed to delete group');
    }
  };

  const clearGroupFilter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setGroupFilter('');
  };

  // Uniqueness group handlers
  const handleAddUniquenessGroup = async () => {
    if (!newUniquenessGroupName.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    setAddingNewGroup(true);
    try {
      await adminAPI.createUniquenessGroup(newUniquenessGroupName.trim());
      toast.success(`Created group "${newUniquenessGroupName.trim()}"`);
      setNewUniquenessGroupName('');
      // Refresh the overview and groups list
      fetchUniquenessGroups();
      const overviewRes = await adminAPI.getUniquenessGroupsOverview();
      setUniquenessOverview(overviewRes.data);
    } catch (err) {
      console.error('Failed to create uniqueness group:', err);
      const message = err.response?.data?.detail || 'Failed to create group';
      toast.error(message);
    } finally {
      setAddingNewGroup(false);
    }
  };

  const handleDeleteUniquenessGroup = async (groupName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${groupName}"? Meals will have this group removed.`)) return;
    
    try {
      const res = await adminAPI.deleteUniquenessGroup(groupName);
      toast.success(res.data.message);
      // Refresh the overview and groups list
      fetchUniquenessGroups();
      const overviewRes = await adminAPI.getUniquenessGroupsOverview();
      setUniquenessOverview(overviewRes.data);
      if (uniquenessGroupFilter === groupName) setUniquenessGroupFilter('');
    } catch (err) {
      console.error('Failed to delete uniqueness group:', err);
      const message = err.response?.data?.detail || 'Failed to delete group';
      toast.error(message);
    }
  };

  const handleUpdateGroupSettings = async (groupName, noRepeatSameDay, cooldownDays) => {
    try {
      await adminAPI.updateUniquenessGroup(groupName, noRepeatSameDay, cooldownDays);
      toast.success(`Updated settings for "${groupName}"`);
      // Refresh the groups list to show updated settings
      fetchUniquenessGroups();
    } catch (err) {
      console.error('Failed to update group settings:', err);
      const message = err.response?.data?.detail || 'Failed to update settings';
      toast.error(message);
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  const getMealTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'breakfast': return 'bg-amber-500/10 text-amber-600';
      case 'lunch': return 'bg-green-500/10 text-green-600';
      case 'dinner': return 'bg-blue-500/10 text-blue-600';
      case 'snack': return 'bg-purple-500/10 text-purple-600';
      default: return 'bg-gray-500/10 text-gray-600';
    }
  };

  const publishedCount = meals.filter(m => m.status === 'published').length;
  const adminCount = meals.filter(m => m.source === 'meals_v2').length;
  const legacyCount = meals.filter(m => m.source === 'legacy').length;

  return (
    <div className="min-h-screen bg-background">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        count={mealsToDelete.length}
      />
      
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/settings')} variant="ghost" size="sm" className="rounded-full" data-testid="admin-back-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="flex items-center gap-2">
                <ChefHat size={24} className="text-accent" />
                <h1 className="font-heading font-bold text-xl">Admin Meal Editor</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={toggleSelectMode}
                variant={selectMode ? "default" : "outline"}
                size="sm"
                className={selectMode ? "bg-accent" : ""}
              >
                {selectMode ? <X size={16} className="mr-1" /> : <CheckSquare size={16} className="mr-1" />}
                {selectMode ? 'Cancel' : 'Select'}
              </Button>
              {selectMode && (
                <Button
                  onClick={selectAll}
                  variant="outline"
                  size="sm"
                  data-testid="select-all-btn"
                >
                  <CheckSquare size={16} className="mr-1" />
                  {meals.every(m => selectedMeals.has(m.meal_id)) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
              <Button
                onClick={() => navigate('/admin/tags')}
                variant="outline"
                size="sm"
                data-testid="manage-tags-btn"
              >
                <span className="mr-1">🏷️</span>
                Tags
              </Button>
              <Button
                onClick={() => setShowGroupModal(true)}
                variant="outline"
                size="sm"
              >
                <FolderPlus size={16} className="mr-1" />
                New Group
              </Button>
            </div>
          </div>

          <AdminNav />


          {/* Search and Filter */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search meals by title..."
                className="pl-9 h-10 bg-secondary/30 border-transparent"
                data-testid="admin-search"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-secondary/30 border-transparent text-sm"
              data-testid="admin-source-filter"
            >
              <option value="">All Sources</option>
              <option value="meals_v2">Admin Created</option>
              <option value="legacy">Legacy Meals</option>
            </select>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-secondary/30 border-transparent text-sm"
            >
              <option value="">All Groups</option>
              {groups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.name} ({g.meal_count})</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-secondary/30 border-transparent text-sm"
              data-testid="admin-status-filter"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <select
              value={uniquenessGroupFilter}
              onChange={(e) => setUniquenessGroupFilter(e.target.value)}
              className="h-10 px-3 rounded-lg bg-blue-500/20 border-blue-500/30 text-sm"
              data-testid="uniqueness-group-filter"
            >
              <option value="">All Repeat Rules</option>
              {uniquenessGroups.map(g => (
                <option key={g.name} value={g.name}>{g.name} ({g.meal_count})</option>
              ))}
            </select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowUniquenessOverview(true)}
              className="h-10 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            >
              <RefreshCcw size={14} className="mr-1" />
              Repeat Rules
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectMode && selectedMeals.size > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="sticky top-[140px] z-10 bg-accent/10 border-b border-accent/30"
          >
            <div className="p-3 flex items-center justify-between">
              <span className="text-sm font-medium">{selectedMeals.size} selected</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAddToGroupModal(true)}>
                  <Folder size={14} className="mr-1" />
                  Add to Group
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddToRepeatRulesModal(true)}>
                  <RefreshCcw size={14} className="mr-1" />
                  Add to Repeat Rules
                </Button>
                <Button type="button" size="sm" variant="destructive" onClick={(e) => handleBulkDelete(e)}>
                  <Trash2 size={14} className="mr-1" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Create + Bulk Import buttons */}
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <Button onClick={() => navigate('/admin/meals/new')} className="h-12 rounded-xl bg-accent text-white" data-testid="create-meal-btn">
            <Plus size={18} className="mr-2" />
            Create New Meal
          </Button>
          <Button
            onClick={() => navigate('/admin/meals/import')}
            variant="outline"
            className="h-12 rounded-xl"
            data-testid="bulk-import-btn"
            title="Bulk import meals from a CSV"
          >
            <Upload size={18} className="mr-2" />
            Import
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-card border border-border/40 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{meals.length}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <button 
            onClick={() => selectAllInSource('meals_v2')} 
            disabled={!selectMode}
            className={`bg-accent/10 border border-accent/30 rounded-xl p-3 text-center transition-colors ${
              selectMode ? 'hover:bg-accent/20 cursor-pointer' : 'cursor-default'
            }`}
          >
            <p className="text-2xl font-bold text-accent">{adminCount}</p>
            <p className="text-xs text-muted-foreground">{selectMode ? 'Select All Admin' : 'Admin'}</p>
          </button>
          <button 
            onClick={() => selectAllInSource('legacy')} 
            disabled={!selectMode}
            className={`bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center transition-colors ${
              selectMode ? 'hover:bg-blue-500/20 cursor-pointer' : 'cursor-default'
            }`}
          >
            <p className="text-2xl font-bold text-blue-600">{legacyCount}</p>
            <p className="text-xs text-muted-foreground">{selectMode ? 'Select All Legacy' : 'Legacy'}</p>
          </button>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{publishedCount}</p>
            <p className="text-xs text-muted-foreground">Published</p>
          </div>
        </div>

        {/* Meal Groups */}
        {groups.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {groups.map(group => {
              const isActive = groupFilter === group.group_id;
              return (
                <div
                  key={group.group_id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors whitespace-nowrap ${
                    isActive 
                      ? 'bg-accent/20 border-accent' 
                      : 'bg-card border-border/40 hover:border-border'
                  }`}
                >
                  <button
                    onClick={() => {
                      if (selectMode) {
                        selectAllInGroup(group.group_id);
                      } else {
                        setGroupFilter(isActive ? '' : group.group_id);
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                    <span className="text-sm font-medium">{group.name}</span>
                    <span className="text-xs text-muted-foreground">({group.meal_count})</span>
                  </button>
                  {isActive && !selectMode && (
                    <button 
                      onClick={clearGroupFilter}
                      className="p-0.5 hover:bg-red-500/10 rounded ml-1"
                      data-testid={`clear-group-filter-${group.group_id}`}
                    >
                      <X size={14} className="text-muted-foreground hover:text-red-500" />
                    </button>
                  )}
                  {!selectMode && (
                    <button 
                      onClick={(e) => handleDeleteGroup(group.group_id, e)}
                      className="p-0.5 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 size={12} className="text-muted-foreground hover:text-red-500" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Meals List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : meals.length === 0 ? (
          <div className="text-center py-12">
            <ChefHat size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No meals found</p>
            <p className="text-sm text-muted-foreground">Create your first meal to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {meals.map((meal) => {
              const isSelected = selectedMeals.has(meal.meal_id);
              const groupInfo = groups.find(g => g.group_id === meal.group_id);
              const macros = meal.computed_macros || {};
              
              return (
                <motion.div
                  key={meal.meal_id}
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-card border rounded-xl overflow-hidden hover:border-border/60 transition-all cursor-pointer ${
                    isSelected ? 'border-accent bg-accent/5' : 'border-border/40'
                  }`}
                  onClick={() => selectMode ? toggleMealSelection(meal.meal_id) : navigate(`/admin/meals/${meal.meal_id}/edit`)}
                  data-testid={`meal-row-${meal.meal_id}`}
                >
                  <div className="flex gap-3 p-3">
                    {/* Selection Checkbox */}
                    {selectMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleMealSelection(meal.meal_id); }}
                        className="flex-shrink-0 self-center"
                      >
                        {isSelected ? (
                          <CheckSquare size={24} className="text-accent" />
                        ) : (
                          <Square size={24} className="text-muted-foreground" />
                        )}
                      </button>
                    )}
                    
                    {/* Thumbnail */}
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-secondary/50">
                      {meal.image_url ? (
                        <img src={meal.image_url} alt={meal.title} className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect fill="#333" width="64" height="64"/><text fill="#888" font-size="24" x="50%" y="55%" text-anchor="middle">?</text></svg>`)}`; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <ChefHat size={24} />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getMealTypeColor(meal.meal_type)}`}>
                          {cap(meal.meal_type)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          meal.source === 'legacy' ? 'bg-blue-500/10 text-blue-600' : 'bg-accent/10 text-accent'
                        }`}>
                          {meal.source === 'legacy' ? 'Legacy' : 'Admin'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          meal.status === 'published' ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500'
                        }`}>
                          {cap(meal.status)}
                        </span>
                        {groupInfo && (
                          <span 
                            className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                            style={{ backgroundColor: `${groupInfo.color}20`, color: groupInfo.color }}
                          >
                            {groupInfo.name}
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-sm truncate">{meal.title}</p>
                      
                      {/* Full Macro Breakdown */}
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {meal.servings} serv
                        </span>
                        {meal.cook_time_min > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {meal.cook_time_min} min
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-orange-500">
                          <Flame size={12} />
                          {macros.calories || 0} cal
                        </span>
                        <span className="flex items-center gap-1 text-red-500">
                          <Beef size={12} />
                          {macros.protein || 0}g P
                        </span>
                        <span className="flex items-center gap-1 text-amber-500">
                          <Wheat size={12} />
                          {macros.carbs || 0}g C
                        </span>
                        <span className="flex items-center gap-1 text-blue-500">
                          <Droplet size={12} />
                          {macros.fat || 0}g F
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {!selectMode && (
                      <div className="flex flex-col justify-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/meals/${meal.meal_id}/edit`); }}
                          className="p-2 rounded-lg hover:bg-secondary/50 transition-colors" title="Edit" data-testid={`edit-${meal.meal_id}`}>
                          <Edit size={16} className="text-accent" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); window.open(`/meal-view/${meal.meal_id}`, '_blank'); }}
                          className="p-2 rounded-lg hover:bg-secondary/50 transition-colors" title="Preview Meal">
                          <ExternalLink size={16} className="text-green-500" />
                        </button>
                        <button onClick={(e) => handleDuplicate(meal.meal_id, e)}
                          className="p-2 rounded-lg hover:bg-secondary/50 transition-colors" title="Duplicate" data-testid={`duplicate-${meal.meal_id}`}>
                          <Copy size={16} className="text-blue-500" />
                        </button>
                        <button onClick={(e) => handleDelete(meal.meal_id, meal.title, e)}
                          className="p-2 rounded-lg hover:bg-red-500/10 transition-colors" title="Delete" data-testid={`delete-${meal.meal_id}`}>
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      <AnimatePresence>
        {showGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowGroupModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4">Create Meal Group</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground">Group Name</label>
                  <Input
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., High Protein, Quick Meals..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Color</label>
                  <div className="flex gap-2 mt-2">
                    {['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'].map(color => (
                      <button
                        key={color}
                        onClick={() => setNewGroupColor(color)}
                        className={`w-8 h-8 rounded-full border-2 ${newGroupColor === color ? 'border-white' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowGroupModal(false)}>Cancel</Button>
                <Button className="flex-1 bg-accent" onClick={handleCreateGroup}>Create Group</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add to Group Modal */}
      <AnimatePresence>
        {showAddToGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddToGroupModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4">Add to Group</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select a group for {selectedMeals.size} selected meals:
              </p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {groups.length === 0 ? (
                  <p className="text-center py-4 text-muted-foreground">
                    No groups yet. Create one first!
                  </p>
                ) : (
                  groups.map(group => (
                    <button
                      key={group.group_id}
                      onClick={() => handleAddToGroup(group.group_id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-border/40 hover:border-accent transition-colors"
                    >
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: group.color }} />
                      <span className="font-medium">{group.name}</span>
                      <span className="text-sm text-muted-foreground ml-auto">({group.meal_count} meals)</span>
                    </button>
                  ))
                )}
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => setShowAddToGroupModal(false)}>
                Cancel
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add to Repeat Rules Modal */}
      <AnimatePresence>
        {showAddToRepeatRulesModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddToRepeatRulesModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <RefreshCcw size={20} className="text-blue-400" />
                  <h3 className="text-lg font-bold">Add to Repeat Rules</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowAddToRepeatRulesModal(false)}>
                  <X size={18} />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Select a repeat rules group for {selectedMeals.size} selected meals:
              </p>
              
              {/* Repeat Rules Group Select */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Repeat Rules Group</label>
                  <select
                    value={selectedRepeatGroup}
                    onChange={e => setSelectedRepeatGroup(e.target.value)}
                    className="w-full p-2 border border-border rounded-lg bg-background"
                  >
                    <option value="">Select a group...</option>
                    {uniquenessGroups.map(group => (
                      <option key={group.name} value={group.name}>{group.name} ({group.meal_count || 0} meals)</option>
                    ))}
                    <option value="__new__">+ Create New Group</option>
                  </select>
                </div>
                
                {/* New Group Name Input */}
                {selectedRepeatGroup === '__new__' && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">New Group Name</label>
                    <Input
                      value={newUniquenessGroupName}
                      onChange={e => setNewUniquenessGroupName(e.target.value)}
                      placeholder="e.g., Smoothies, Salads, Chicken dishes..."
                      className="w-full"
                    />
                  </div>
                )}
                
                {/* No Repeat Same Day Toggle */}
                <div className="flex items-center justify-between p-3 border border-border/40 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">No Repeat Same Day</p>
                    <p className="text-xs text-muted-foreground">Prevent meals in this group from appearing on the same day</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRepeatNoSameDay(!repeatNoSameDay)}
                    className={`w-12 h-6 rounded-full transition-colors ${repeatNoSameDay ? 'bg-accent' : 'bg-gray-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${repeatNoSameDay ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                
                {/* Cooldown Days */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Cooldown Days</label>
                  <p className="text-xs text-muted-foreground mb-2">Days before this meal can repeat</p>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={repeatCooldownDays}
                    onChange={e => setRepeatCooldownDays(parseInt(e.target.value) || 0)}
                    className="w-24"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddToRepeatRulesModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleAddToRepeatRules}
                  disabled={!selectedRepeatGroup || (selectedRepeatGroup === '__new__' && !newUniquenessGroupName.trim())}
                >
                  Apply Rules
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Uniqueness Groups Overview Modal */}
      <AnimatePresence>
        {showUniquenessOverview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowUniquenessOverview(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <RefreshCcw size={20} className="text-blue-400" />
                  <h3 className="text-lg font-bold">Repeat Rules Overview</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowUniquenessOverview(false)}>
                  <X size={18} />
                </Button>
              </div>
              
              {/* Add New Group Section */}
              <div className="mb-4 p-3 bg-secondary/30 rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <Input
                    value={newUniquenessGroupName}
                    onChange={(e) => setNewUniquenessGroupName(e.target.value)}
                    placeholder="Enter new group name (e.g., Parfaits)"
                    className="flex-1 h-9"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newUniquenessGroupName.trim()) {
                        handleAddUniquenessGroup();
                      }
                    }}
                  />
                  <Button 
                    size="sm" 
                    onClick={handleAddUniquenessGroup}
                    disabled={!newUniquenessGroupName.trim() || addingNewGroup}
                    className="h-9 bg-blue-500 hover:bg-blue-600"
                  >
                    {addingNewGroup ? 'Adding...' : <><Plus size={14} className="mr-1" />Add Group</>}
                  </Button>
                </div>
              </div>
              
              {!uniquenessOverview ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-blue-300">
                      <strong>{uniquenessOverview.total_meals_with_rules}</strong> meals have repeat rules configured
                    </p>
                  </div>
                  
                  {uniquenessOverview.groups?.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">No uniqueness groups configured yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {uniquenessOverview.groups?.map(groupName => {
                        // Find group data from the list
                        const groupData = uniquenessGroups.find(g => g.name === groupName) || {};
                        const mealsInGroup = uniquenessOverview.meals_by_group?.[groupName] || [];
                        const isDefault = uniquenessOverview.default_groups?.includes(groupName);
                        const isCustom = uniquenessOverview.custom_groups?.includes(groupName);
                        
                        return (
                          <div key={groupName} className="border border-border rounded-lg overflow-hidden">
                            {/* Group Header */}
                            <div className="p-3 bg-secondary/30">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{groupName}</span>
                                  {isDefault && (
                                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">Default</span>
                                  )}
                                  {isCustom && !isDefault && (
                                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded">Custom</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">{groupData.meal_count || mealsInGroup.length} meals</span>
                                  {!isDefault && (
                                    <button
                                      onClick={(e) => handleDeleteUniquenessGroup(groupName, e)}
                                      className="p-1 hover:bg-red-500/20 rounded text-red-400 hover:text-red-300"
                                      title="Delete group"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              
                              {/* Group Settings */}
                              <div className="flex items-center gap-4 pt-2 border-t border-border/50">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={groupData.no_repeat_same_day || false}
                                    onChange={(e) => handleUpdateGroupSettings(groupName, e.target.checked, groupData.cooldown_days)}
                                    className="w-4 h-4 rounded"
                                  />
                                  <span className="text-muted-foreground">Prevent same-day</span>
                                </label>
                                
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-muted-foreground">Cooldown:</span>
                                  <select
                                    value={groupData.cooldown_days ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      handleUpdateGroupSettings(groupName, groupData.no_repeat_same_day || false, val === '' ? null : parseInt(val, 10));
                                    }}
                                    className="h-7 px-2 rounded bg-secondary/50 text-sm border border-border/50"
                                  >
                                    <option value="">Default</option>
                                    <option value="0">0 (next day OK)</option>
                                    <option value="1">1 day</option>
                                    <option value="2">2 days</option>
                                    <option value="3">3 days</option>
                                    <option value="5">5 days</option>
                                    <option value="7">7 days</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                            
                            {/* Meals in this group */}
                            {(groupData.meal_count > 0 || mealsInGroup.length > 0) && (
                              <div className="p-2 space-y-1 max-h-32 overflow-y-auto border-t border-border/30">
                                {mealsInGroup.slice(0, 8).map(meal => (
                                  <div 
                                    key={meal.meal_id}
                                    className="flex items-center justify-between text-sm p-2 rounded hover:bg-secondary/30 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/admin/meals/${meal.meal_id}/edit`);
                                      setShowUniquenessOverview(false);
                                    }}
                                  >
                                    <span className="truncate flex-1 text-muted-foreground">{meal.title}</span>
                                  </div>
                                ))}
                                {mealsInGroup.length > 8 && (
                                  <p className="text-xs text-muted-foreground text-center py-1">
                                    +{mealsInGroup.length - 8} more
                                  </p>
                                )}
                                {mealsInGroup.length === 0 && groupData.meal_count === 0 && (
                                  <p className="text-xs text-muted-foreground text-center py-2">No meals in this group yet</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      <strong>How it works:</strong> Meals in the same uniqueness group with "Prevent same-day" enabled 
                      won't appear twice on the same day. Cooldown days controls how soon the same meal can reappear 
                      (0 = next day OK, higher = wait longer).
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminMealList;
